import { useLayoutEffect, useRef } from 'react';
import { cn } from '../../lib/cn.ts';

export interface LiveCaptionProps {
  /** The words so far. They grow at the end; a new line of speech replaces them. */
  text: string;
  className?: string;
}

/**
 * Subtitles for a voice, three lines at a time.
 *
 * The newest words sit on the bottom line and older lines slide up and out, the
 * way live captions read. The window is exactly three line heights tall, so a
 * line is never cut through its letters.
 */
export function LiveCaption({ text, className }: LiveCaptionProps) {
  const frame = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const element = frame.current;
    if (element === null) {
      return;
    }

    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    element.scrollTo({
      top: element.scrollHeight,
      behavior: reduced ? 'auto' : 'smooth',
    });
  }, [text]);

  return (
    <div
      ref={frame}
      data-slot='live-caption'
      className={cn(
        'h-[calc(3*var(--text-md)*var(--text-md--line-height))] w-full overflow-hidden',
        className,
      )}
    >
      {/* Short text sits on the bottom line, where the next words will appear. */}
      <div className='flex min-h-full flex-col justify-end'>
        <p className='text-left text-md text-ink-body'>{text}</p>
      </div>
    </div>
  );
}
